import React, { useRef, useState, useMemo, forwardRef, useCallback } from 'react';
import { isEmpty, findIndex, forEach, isUndefined, isArray, map } from 'lodash';
import { Form, Tooltip } from 'antd'
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import GenericMappingForm from '../GenericMappingForm';

const integrationTypes = [{
    label: 'Pipeline',
    value: 'pipeline'
}, {
    label: 'Runtime',
    value: 'runtime'
}];


const SysDigMappingForm = forwardRef(({ disableProjectFields = false, ...props }, ref) => {
    const [form] = Form.useForm();
    const projectRef = useRef({ pipeline: [], runtime: [], compliance: []});
    const IntRef = useRef({})
    const [integrationTypeOption, setIntegrationTypeOption] = useState({});
    const [mappedProjectList] = useState(() => {
        if (props.record) {
            const { resultId, resultName, imageId, imageName, taskId, taskName, imageCluster, imageNamespace } = props.record;
            const refIdArr = [resultId?.split(","), imageId?.split(","), taskId?.split(",")];
            const refNameArr = [resultName?.split(","), imageName?.split(","), taskName?.split(",")]
            const validIdIndex = findIndex(refIdArr, (value) => !isEmpty(value))
            return {
                ...(validIdIndex !== -1 ? {[integrationTypes[validIdIndex].value]: refIdArr[validIdIndex].map((id, index)=>({
                    id,
                    name: refNameArr[validIdIndex][index],
                    otherProperties: {
                        clusterName: imageCluster,
                        nameSpaceName: imageNamespace
                    }
                }))} : {})
            }
        }
        return { pipeline: [], runtime: [], compliance: [] }
    })

    const [initialValues] = useState(() => {
        if (props.record) {
          return [];
        }
        return [
          {
            imageNamespaceAll: true,
            product: props.defaultProduct,
            subProduct: props.defaultSubProduct,
            subProductId: props.defaultSubProductId,
            environment: props.defaultEnvironment,
          },
        ];
      });

    const fetchProjects = useCallback((params) => {
        const { loginConfigId, integrationType, imageCluster, imageNamespace,imageNamespaceAll } = params;
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: loginConfigId, type: integrationType, ...params });
        promise.then((response) => {
            const { projects } = response;
            if(integrationType !== 'runtime' || (!isEmpty(imageCluster) && (!isEmpty(imageNamespace) ^ imageNamespaceAll))){
                if(isUndefined(mappedProjectList[integrationType])){
                    mappedProjectList[integrationType] = [];
                }
                if (mappedProjectList[integrationType].length > 0 && !isEmpty(response.projects)) {
                    const mappedProject = mappedProjectList[0];
                    if(mappedProject && mappedProject.otherProperties?.clusterName === imageCluster && mappedProject.otherProperties?.nameSpaceName === imageNamespace){
                        response.projects = [...response.projects, ...mappedProjectList[integrationType]];
                    }
                }
                forEach(projects, p => {
                    projectRef.current[integrationType][p.id] = p.name;
                });
            }
        });
        return { promise, cancel };

    }, [props.toolName, mappedProjectList])


    const parseExtraFieldsDataFromResponse = useCallback((config) => {
        const { resultId, imageId, taskId, imageCluster, imageNamespace, imageNamespaceAll } = config;
        const refIdArr = [resultId, imageId, taskId];
        let validIdIndex = findIndex(refIdArr, (value) => !isEmpty(value))
        if(!isEmpty(imageCluster)){
            setIntegrationTypeOption((prevIntegrationTypeOption)=>({...prevIntegrationTypeOption, 0:"runtime"}));
            validIdIndex = 1;
        }
        return ({
            projectId: isEmpty(refIdArr[validIdIndex]) || refIdArr[validIdIndex] === "All" ? [] : refIdArr[validIdIndex].split(','),
            imageCluster: isEmpty(imageCluster) || imageCluster === "All" ? [] : imageCluster.split(','),
            imageNamespace: isEmpty(imageNamespace) ? [] : imageNamespace.split(','),
            imageNamespaceAll: imageNamespaceAll === 'true' ? true : false,
            integration: integrationTypes[validIdIndex].value
        });
    }, []);

    const onIntegrationTypeChange = useCallback((value, field) => {
        const { name } = field;
        const configurations = form.getFieldValue("configurations") || [];
        let updatedConfiguration = [
            ...configurations,
        ];

        updatedConfiguration[name] = {
            ...updatedConfiguration[name],
            integration: value,
            imageCluster: undefined,
            imageNamespace: undefined,
            projectId: undefined
        }
        setIntegrationTypeOption((prevIntegrationTypeOption)=>({...prevIntegrationTypeOption, [name]:value}));
        form.setFieldsValue({ configurations: updatedConfiguration });
    }, [form]);

    const onImageClusterChange = useCallback((value, field) => {
        const { name } = field;
        const configurations = form.getFieldValue("configurations") || [];
        let updatedConfiguration = [
            ...configurations,
        ];

        updatedConfiguration[name] = {
            ...updatedConfiguration[name],
            imageCluster: value,
            imageNamespace: undefined,
            projectId: undefined
        }
        IntRef.current[`${integrationTypeOption[name]}-namespace-${name}`] = undefined;
        IntRef.current[`${integrationTypeOption[name]}-image-${name}`] = undefined;
        form.setFieldsValue({ configurations: updatedConfiguration });
    }, [form, integrationTypeOption]);

    const onImageNamespaceChange = useCallback((value, field) => {
        const { name } = field;
        const configurations = form.getFieldValue("configurations") || [];
        let updatedConfiguration = [
            ...configurations,
        ];

        updatedConfiguration[name] = {
            ...updatedConfiguration[name],
            imageNamespace: value,
            imageNamespaceAll: undefined,
            projectId: undefined
        }
        IntRef.current[`${integrationTypeOption[name]}-image-${name}`] = undefined;
        form.setFieldsValue({ configurations: updatedConfiguration });
    }, [form, integrationTypeOption]);

    const onImageNamespaceAllChange = useCallback((e) => {
        const { name } = e.target.listField;
        const { checked } = e.target;
        const configurations = form.getFieldValue("configurations") || [];
        let updatedConfiguration = [
            ...configurations
        ];
        updatedConfiguration[name] = {
            ...updatedConfiguration[name],
            imageNamespaceAll: checked,
            imageNamespace: undefined,
            projectId: undefined
        }
        IntRef.current[`${integrationTypeOption[name]}-image-${name}`] = undefined;
        form.setFieldsValue({ configurations: updatedConfiguration })
    }, [form, integrationTypeOption]);

    const getExtraFieldsData = useCallback((config) => {
        let { projectId, integration, imageCluster, imageNamespace, imageNamespaceAll } = config;
        const selectedProject = [];
        if(!isArray(projectId)){
            projectId = [projectId];
        }
        projectId = projectId.filter(item => item !== undefined);
        projectId.forEach(id => {
            selectedProject.push(projectRef.current[integration][id]);
        })

        const extraFieldData = {}
        switch (integration) {
            case 'pipeline':
                extraFieldData.resultId = isEmpty(projectId) ? "All" : projectId.join(',')
                extraFieldData.resultName = isEmpty(selectedProject) ? "All" : selectedProject.join(',')

                break;
            case 'runtime':
                extraFieldData.imageCluster = (isEmpty(projectId) && isEmpty(selectedProject) && isEmpty(imageCluster) && isEmpty(imageNamespace)) ? 'All' : (imageCluster || []).join(',');
                extraFieldData.imageId = (projectId || []).join(',');
                extraFieldData.imageName = (selectedProject || []).join(',');
                extraFieldData.imageNamespace = (imageNamespace || []).join(',');
                extraFieldData.imageNamespaceAll = imageNamespaceAll ? true : false
                break;

            case 'compliance':
                extraFieldData.taskId = projectId.join(',')
                extraFieldData.taskName = selectedProject.join(',')

                break;
            default:
                return;
        }
        return extraFieldData
    }, []);


    const projectListRender = useCallback(
        (listField) => {
          const getSysdigFields = () => {
            const fields = [
                {
                    name: 'integration',
                    colSpan: '10',
                    type: FieldTypes.SELECT,
                    label: 'Integration Type',
                    options: integrationTypes,
                    disabled: disableProjectFields,
                    required: true,
                    props: {
                        onChange: onIntegrationTypeChange,
                        autoFocus: !disableProjectFields
                    }
                },
                ...( integrationTypeOption && integrationTypeOption[listField.name] === 'runtime' ? [
                    {
                        name: '',
                        type: FieldTypes.LABEL,
                        colSpan: 14
                    },
                    {
                        name: 'imageCluster',
                        colSpan: '8',
                        type: FieldTypes.SELECT,
                        label: 'Clusters',
                        required: false,
                        disabled: disableProjectFields,
                        props: {
                            onChange: onImageClusterChange,
                            autoFocus: !disableProjectFields,
                            setExtraProps: (field, listField) => {
                                const allConfigs = form.getFieldsValue().configurations;
                                const integrationType = allConfigs[listField.name]?.integration;
                                const clusterUniqueKey = `${integrationType}-cluster-${listField.name}`;
                                if(integrationType){
                                    if(!IntRef.current[clusterUniqueKey]){
                                        IntRef.current[clusterUniqueKey] = { login_id: props.loginConfigId, integrationType};
                                    }
                                    return {
                                        mode: "multiple",
                                        api: fetchProjects,
                                        apiParams: IntRef.current[clusterUniqueKey],
                                        resetOptionsProp: integrationType,
                                        parseSelectOptions: parseImageOptions
                                    }
                                }
                                else{
                                    return {
                                        disabled: true
                                    }
                                }
                            }
                        }
                    },
                    {
                        name: 'imageNamespace',
                        colSpan: '8', 
                        type: FieldTypes.SELECT,
                        label: 'Namespace/Resource',
                        required: false,
                        disabled: disableProjectFields,
                        props: {
                            onChange: onImageNamespaceChange,
                            autoFocus: !disableProjectFields,
                            setExtraProps: (field, listField) => {
                                const allConfigs = form.getFieldsValue().configurations;
                                const integrationType = allConfigs[listField.name]?.integration;
                                const imageCluster = allConfigs[listField.name]?.imageCluster;
                                const imageNamespaceAll = allConfigs[listField.name]?.imageNamespaceAll;
                                let project = {};
                                if(!isEmpty(imageCluster)){
                                    project["imageCluster"] = imageCluster.join(",");
                                }
                                const namespaceUniqueKey = `${integrationType}-namespace-${listField.name}`;
                                if(integrationType && (integrationType === "runtime" ? !isEmpty(imageCluster) : true)){
                                    if(!IntRef.current[namespaceUniqueKey]){
                                        IntRef.current[namespaceUniqueKey] = { login_id: props.loginConfigId, integrationType, project : JSON.stringify({...project}), ...project };
                                    }
                                    return {
                                        mode: "multiple",
                                        api: fetchProjects,
                                        apiParams: IntRef.current[namespaceUniqueKey],
                                        resetOptionsProp: imageCluster,
                                        parseSelectOptions: parseImageOptions,
                                        disabled: isEmpty(imageCluster) || imageNamespaceAll || disableProjectFields
                                    }
                                }
                                else{
                                    return {
                                        disabled: true
                                    }
                                }
                            }
                        }
                    },
                    {
                        name: 'imageNamespaceAll',
                        colSpan: '8',
                        type: FieldTypes.BOOLEAN,
                        label: 'Select all Namespaces',
                        inline: true,
                        className: "m-t-l",
                        props: {
                            onChange: onImageNamespaceAllChange,
                            autoFocus: !disableProjectFields,
                            setExtraProps: (field, listField) => {
                                const allConfigs = form.getFieldsValue().configurations;
                                const imageCluster = allConfigs[listField.name]?.imageCluster;
                                return {
                                    disabled: isEmpty(imageCluster) || disableProjectFields
                                }
                            }
                        }
                      },
                ] : []),
                {
                    name: 'projectId',
                    colSpan: "12",
                    type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
                    label: integrationTypeOption[listField.name] === "runtime" ? 'Image Name' : 'SysDig Image/Task',
                    required: (integrationTypeOption[listField.name] === "compliance"),
                    disabled: disableProjectFields,
                    props: {
                        autoFocus: !disableProjectFields,
                        setExtraProps: (field, listField) => {
                            const allConfigs = form.getFieldsValue().configurations;
                            const integrationType = allConfigs[listField.name]?.integration;
                            const imageCluster = allConfigs[listField.name]?.imageCluster;
                            const imageNamespace = allConfigs[listField.name]?.imageNamespace;
                            let project = {};
                            if(!isEmpty(imageCluster)){
                                project["imageCluster"] = imageCluster.join(",");
                            }
                            if(!isEmpty(imageNamespace)){
                                project["imageNamespace"] = imageNamespace.join(",");
                            }
                            else{
                                project["imageNamespaceAll"] = true;
                            }
                            const imageUniqueKey = `${integrationType}-image-${listField.name}`;
                            if(integrationType && (integrationType === "runtime" ? !isEmpty(imageCluster) : true)){
                                if(!IntRef.current[imageUniqueKey]){
                                    IntRef.current[imageUniqueKey] = { login_id: props.loginConfigId, integrationType, project : JSON.stringify({...project}), ...project };
                                }
                                return {
                                    mode: (integrationType === 'runtime' || (integrationType === 'pipeline'))
                                      ? "multiple"
                                      : "single",
                                    api: integrationType && fetchProjects,
                                    apiParams: IntRef.current[imageUniqueKey],
                                    resetOptionsProp: imageNamespace,
                                    parseSelectOptions: integrationType === 'runtime' || (integrationType === "pipeline") ? parseImageOptions : undefined,
                                    disabled: (integrationType === 'runtime' ? isEmpty(imageCluster) : false) || disableProjectFields,
                                    ...(integrationType === "pipeline" && { placeholder: "Leave blank to select all" })
                                }
                            }
                            else{
                                return {
                                    disabled: true
                                }
                            }
                        },
                        optionFilterProp: "labelStr"
                    }
                },
            ];
            return generateFormFields(fields, 24, listField);
          };
          return <div className="m-b-sm">{getSysdigFields()}</div>;
        },
        [disableProjectFields, onIntegrationTypeChange, integrationTypeOption, onImageClusterChange, onImageNamespaceChange, onImageNamespaceAllChange, form, fetchProjects, props.loginConfigId]
      );

    const extraFields = useMemo(() => ([
        {
            name: (listField) => [listField.name, "projectList"],
            render: projectListRender,
        },
    ]), [projectListRender]);


    return (
        <GenericMappingForm
            form={form}
            ref={ref}
            getExtraFieldsData={getExtraFieldsData}
            extraFields={extraFields}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
            formDefaultValues={initialValues}
            newRowDefaultValue={initialValues[0]}
            {...props}
        />
    )
})

export default SysDigMappingForm;

const parseImageOptions = (options) => {
    if(options.projects !== undefined){
        options = options.projects;
    }
    return map(options, (option) => {
        let { label = option, value = option, title = option } = option;
        label = option.name.split('>')[0];
        title = option.name
        value = option.id; 
        return { label: <Tooltip title={title} placement="topLeft">{label}</Tooltip>, value, labelStr: label };
    })
}
